import { AxiosError } from 'axios';
import { v4 as uuidv4 } from 'uuid';
import { EOrderTab } from '../../store/ducks/long-steel-orders/types';
import {
  ILocalStorage,
  ILocalStorageObj,
  IHeaderRequest,
  ELocalStorageKey,
  IHeaderRequestNonLoggedInArea
} from './types';

export class EGerdauService implements ILocalStorage {
  private accessTokenCookieKey = '@access_token';
  private commerceTokenCookieKey = '@commerce_token';
  private ssoAccessCookieKey = '@sso-access';
  private authorizationTokenKey = '@authorization-token';
  private profileAccessToken = '@Profile-Access-Token';
  private correlation_id = '@x-correlation-id';

  getLocalStorage(key: string) {
    const storageValue = localStorage.getItem(key);

    if (storageValue) {
      return JSON.parse(storageValue) as ILocalStorageObj;
    } else {
      return undefined;
    }
  }

  setLocalStorage(
    key: string,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    obj: any,
    customerId = '',
    tab?: EOrderTab | undefined
  ): void {
    const localStorageObj: ILocalStorageObj = {
      obj,
      dateTime: new Date().getTime(),
      customerId,
      tab
    };

    localStorage.setItem(key, JSON.stringify(localStorageObj));
  }

  getSsoAccess(): boolean {
    return this.getValueCookie(this.ssoAccessCookieKey) === 'true';
  }

  getToken(): string | undefined {
    const cookiesList = String(document.cookie).split(';');
    const tokenCookie = this.accessTokenCookieKey + '=';
    for (const cookie of cookiesList) {
      if (
        cookie.substring(0, cookie.indexOf('=')).trim() ===
        this.accessTokenCookieKey
      ) {
        return decodeURIComponent(
          cookie.trim().substring(tokenCookie.length, cookie.length)
        );
      }
    }
  }

  getSecurityToken(): string | undefined {
    return this.getValueCookie(this.authorizationTokenKey);
  }

  getProfileAccessToken(): string | undefined {
    return this.getValueCookie(this.profileAccessToken);
  }

  getValueCookie(key: string): string | undefined {
    const cookiesList = String(document.cookie).split(';');
    const cookieKey = key + '=';
    for (const cookie of cookiesList) {
      if (cookie.substring(0, cookie.indexOf('=')).trim() === key) {
        return decodeURIComponent(
          cookie.trim().substring(cookieKey.length, cookie.length)
        );
      }
    }
  }

  static getTokenMaxAgeInSeconds(token?: string): number {
    if (!token) {
      return 0;
    }
    try {
      const payloadBase64Url = token.split('.')[1];

      if (!payloadBase64Url) {
        return 0;
      }

      let base64 = payloadBase64Url.replace(/-/g, '+').replace(/_/g, '/');
      base64 += '='.repeat((4 - (base64.length % 4)) % 4);

      const payloadJson = decodeURIComponent(
        atob(base64)
          .split('')
          .map(function (c) {
            return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
          })
          .join('')
      );

      const payload = JSON.parse(payloadJson);

      if (!payload.exp || typeof payload.exp !== 'number') {
        return 0;
      }

      const expInSeconds = payload.exp || 0;
      const nowInSeconds = Math.floor(Date.now() / 1000);

      const durationInSeconds = expInSeconds - nowInSeconds;
      return Math.max(0, durationInSeconds);
    } catch (e) {
      return 0;
    }
  }

  public isTokenExpired(): boolean {
    return !this.getValueCookie('@commerce_token');
  }

  // Adobe token GR-600
  // Prepare headers to receive props withCommerceToken to use x-commerce-token conditional
  getHeaders({
    withCommerceToken = false
  }: {
    withCommerceToken?: boolean;
  } = {}): IHeaderRequest {
    const token = this.getValueCookie(this.accessTokenCookieKey);
    const client_id = process.env.REACT_APP_oauthClientID as string;
    const client_secret = process.env.REACT_APP_oauthClientSecret as string;
    const ssoAccess = this.getValueCookie(this.ssoAccessCookieKey);
    const authorization_token = this.getValueCookie(this.authorizationTokenKey);
    const profile_access_token = this.getValueCookie(this.profileAccessToken);
    const current_correlation_id = this.getValueCookie(this.correlation_id);
    const commerce_token = this.getValueCookie(this.commerceTokenCookieKey); //Adobe token GR-600'

    const correlation_id =
      current_correlation_id ?? EGerdauService.setCorrelationId(uuidv4());

    return {
      Authorization: `Bearer ${authorization_token}`,
      client_id,
      client_secret,
      sso_access: `${ssoAccess}`,
      'Profile-Access-Token': `Bearer ${profile_access_token ?? token}`,
      'x-correlation-id': `${correlation_id}`,
      'x-related-path': EGerdauService.getRelatedPath(),
      ...(!!commerce_token &&
        withCommerceToken && { 'x-commerce-token': `Bearer ${commerce_token}` })
    };
  }

  getHeadersNonLoggedInArea(): IHeaderRequestNonLoggedInArea {
    const client_id = process.env.REACT_APP_oauthClientID as string;
    const client_secret = process.env.REACT_APP_oauthClientSecret as string;
    const authorization_token = this.getValueCookie(this.authorizationTokenKey);
    const correlation_id = EGerdauService.setCorrelationId(uuidv4());

    return {
      Authorization: `Bearer ${authorization_token}`,
      client_id,
      client_secret,
      'x-correlation-id': `${correlation_id}`
    };
  }

  static setToken(token: string) {
    // sem path=/ o cookie fica restrito à rota atual da SPA e some ao abrir outra aba/rota
    document.cookie = `@access_token=${token}; path=/`;
  }

  static setSsoAccess(ssoAccess: boolean) {
    document.cookie = `@sso-access=${ssoAccess}; path=/`;
  }

  static setProfileAccessToken(profileAccessToken: string) {
    document.cookie = `@Profile-Access-Token=${profileAccessToken}; path=/`;
  }

  //Adobe token GR-600
  static setCommerceToken(commerceToken: string) {
    const durationInSeconds = this.getTokenMaxAgeInSeconds(commerceToken);
    document.cookie = `@commerce_token=${commerceToken};max-age=${durationInSeconds};path=/`;
  }

  static setAuthorizationToken(authorizationToken: string) {
    let domain = '.egerdau.com.br';
    if (window.location.hostname === 'localhost') {
      domain = 'localhost';
    }
    document.cookie = `@authorization-token=${authorizationToken}; path=/; domain=${domain}`;
  }

  static setAuthorizationTokenExpiration(authorizationTokenExpiration: string) {
    let domain = '.egerdau.com.br';
    if (window.location.hostname === 'localhost') {
      domain = 'localhost';
    }
    document.cookie = `@authorization-token-expiration=${authorizationTokenExpiration}; path=/; domain=${domain}`;
  }

  static setCorrelationId(UUID: string) {
    document.cookie = `@x-correlation-id=${UUID}`;
    return UUID;
  }

  static getRelatedPath(): string {
    return `${window.location.pathname ?? ''}`;
  }

  static treatAxiosError(error: AxiosError): void {
    const isUnauthorized =
      error?.response?.status === 401 ||
      (error?.response?.data &&
        JSON.stringify(error.response.data).includes('UNAUTHORIZED'));

    if (isUnauthorized) {
      new EGerdauService().logout();
    }
  }

  static checkAndDeleteCookie(name: string): void {
    const cookies = document.cookie.split(';');
    for (let i = 0; i < cookies.length; i++) {
      const cookie = cookies[i].trim();
      if (cookie.startsWith(`${name}=`)) {
        document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
        break;
      }
    }
  }

  logout() {
    this.cleanLocalStorage();

    if (!window.location.origin.includes('localhost')) {
      // redireciona p/ login se não estiver executando local
      const loginUrl = process.env.REACT_APP_LOGIN_URL;
      if (loginUrl) {
        const newLoginUrl = loginUrl.concat('?logout=true');
        console.warn('redirecting for:' + newLoginUrl);
        setTimeout(() => {
          sessionStorage.clear();
          window.location.assign(newLoginUrl);
        }, 3000);
      }
    }
  }

  clearCookiesAndStorage() {
    const cookies = document.cookie.split(';');

    for (let i = 0; i < cookies.length; i++) {
      const cookie = cookies[i];
      const eqPos = cookie.indexOf('=');
      const name = eqPos > -1 ? cookie.substr(0, eqPos) : cookie;
      document.cookie = name + '=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/';
    }

    localStorage.clear();
    sessionStorage.clear();
    this.cleanLocalStorage();

    // limpa cookies
    const allCookies = String(document.cookie).split(';');
    for (const cookie of allCookies) {
      document.cookie = cookie + '=;expires=' + new Date(0).toUTCString();
    }
  }

  cleanLocalStorage() {
    // limpa localStorage
    Object.values(ELocalStorageKey).forEach((localStorageKey) =>
      window.localStorage.removeItem(localStorageKey)
    );
  }
}
